// client/src/views/admin/users/customers/view/tabs/SecurityTab.jsx
import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import Card from '@/components/ui/cards/Card';
import { changeCustomerPasswordService } from '@/services/CustomerServices';

function decodeJwtPayload(token) {
  try {
    const base = token.split('.')[1];
    const json = atob(base.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    return null;
  }
}

export default function SecurityTab({ customerId }) {
  const dispatch = useDispatch();

  const [oldPass, setOldPass] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  // Determine if a customer token is present AND belongs to this profile
  const { hasCustomerToken, isSelfForThisCustomer, hasAdminToken } = useMemo(() => {
    const tok = localStorage.getItem('customer_jwt') || localStorage.getItem('customerToken');
    const adminTok = !!localStorage.getItem('jwt_token');
    if (!tok) return { hasCustomerToken: false, isSelfForThisCustomer: false, hasAdminToken: adminTok };

    const payload = decodeJwtPayload(tok);
    const jwtCustomerId = payload?.customerId || payload?.customer_id || payload?.id || payload?._id;
    const matches = !customerId || (jwtCustomerId && String(jwtCustomerId) === String(customerId));
    return { hasCustomerToken: true, isSelfForThisCustomer: !!matches, hasAdminToken: adminTok };
  }, [customerId]);

  const isSelfService = hasCustomerToken && isSelfForThisCustomer;

  const showDebugModal = (apiRes, localNewPassword) => {
    const d = apiRes?.data?.data?.debug || {};
    const changed = apiRes?.data?.data?.changed ?? apiRes?.data?.data?.debug?.changed;

    const lines = [
      `<div style="font-family:ui-sans-serif,system-ui; text-align:left">`,
      `<div style="font-weight:600; margin-bottom:6px">Password Change Debug</div>`,
      `<div><b>Mode:</b> ${d.mode || (isSelfService ? 'self' : 'admin')}</div>`,
      `<div><b>Trace ID:</b> ${d.trace || '—'}</div>`,
      `<div><b>Customer ID:</b> ${d.customerId || customerId || '—'}</div>`,
      `<div><b>Changed:</b> ${String(!!changed).toUpperCase()}</div>`,
      `<div><b>New Password Length:</b> ${d.new_password_len ?? (localNewPassword ? localNewPassword.length : '—')}</div>`,
      `<div><b>New Password SHA-256:</b> <code>${d.new_password_sha256 || '—'}</code></div>`,
    ];

    // Only present plaintext echo if backend allowed it via env flag
    if (d.logged_plaintext && d.password_echo) {
      lines.push(`<div style="margin-top:6px;color:#b91c1c"><b>PLAINTEXT (DEV):</b> <code>${d.password_echo}</code></div>`);
    } else {
      // show local typed password masked as a convenience (not from server)
      if (localNewPassword) {
        const preview = `${localNewPassword.slice(0, 2)}${'•'.repeat(Math.max(0, localNewPassword.length - 2))}`;
        lines.push(`<div style="margin-top:6px;color:#334155"><b>Local Preview:</b> <code>${preview}</code></div>`);
      }
      lines.push(`<div style="margin-top:4px;color:#475569"><i>(Set LOG_PLAINTEXT_PASSWORDS=true on the server to echo plaintext in dev.)</i></div>`);
    }

    lines.push(`</div>`);

    Swal.fire({
      title: changed ? 'Saved' : 'Save Attempted',
      html: lines.join(''),
      icon: changed ? 'success' : 'warning',
      confirmButtonText: 'OK',
      confirmButtonColor: '#2563eb',
      width: 560,
    });
  };

  const handleSave = () => {
    if (!password || !confirm) {
      return Swal.fire('Error', 'Please fill in all fields', 'error');
    }
    if (password !== confirm) {
      return Swal.fire('Error', 'New passwords do not match', 'error');
    }

    if (isSelfService) {
      if (!oldPass) {
        return Swal.fire('Error', 'Please enter your current password', 'error');
      }
      return dispatch(changeCustomerPasswordService({ old_password: oldPass, password }))
        .then((res) => {
          showDebugModal(res, password);
          setOldPass(''); setPassword(''); setConfirm('');
        })
        .catch((err) => Swal.fire('Error', err?.message || 'Update failed', 'error'));
    }

    if (!hasAdminToken) {
      return Swal.fire('Unauthorized', 'Please log in as an admin to reset this customer’s password.', 'error');
    }
    if (!customerId) {
      return Swal.fire('Error', 'No customer selected for admin reset.', 'error');
    }

    return dispatch(changeCustomerPasswordService({ password, customerIdForAdminFallback: customerId }))
      .then((res) => {
        showDebugModal(res, password);
        setOldPass(''); setPassword(''); setConfirm('');
      })
      .catch((err) => Swal.fire('Error', err?.message || 'Update failed', 'error'));
  };

  return (
    <Card className="rounded-xl border border-gray-200 bg-white px-6 pt-6 pb-6">
      <div className="flex items-center gap-2 mb-4">
        <i className="lab lab-lock-line text-xl text-slate-700" />
        <h3 className="text-lg font-medium">
          {isSelfService ? 'Change Password' : 'Admin Reset Customer Password'}
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {isSelfService && (
          <div>
            <label className="block text-sm text-gray-500 mb-1">Current Password *</label>
            <input
              type="password"
              className="w-full p-2 border border-gray-200 rounded"
              value={oldPass}
              onChange={(e) => setOldPass(e.target.value)}
            />
          </div>
        )}
        <div>
          <label className="block text-sm text-gray-500 mb-1">New Password *</label>
          <input
            type="password"
            className="w-full p-2 border border-gray-200 rounded"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm text-gray-500 mb-1">Confirm Password *</label>
          <input
            type="password"
            className="w-full p-2 border border-gray-200 rounded"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>
      </div>

      {!isSelfService && (
        <p className="text-sm text-amber-600 mt-3">
          You are using an admin context. This will reset the customer’s password without the old password.
        </p>
      )}

      <button
        onClick={handleSave}
        className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition"
      >
        <i className="lab lab-save-line text-base" />
        Save
      </button>
    </Card>
  );
}
